import Cookies from 'js-cookie'
import { createSlice } from '@reduxjs/toolkit'
import get from 'lodash/get'
import set from 'lodash/set'
import deepClone from 'lodash/cloneDeep'

import { authApi, LoginResponse } from './services/auth'
import { userApi } from './services/user'
import { optionsApi } from './services/options'
import type {
  User,
  Locations,
  Tags,
  JobTitles,
  Events,
  Sessions,
  SubEvents,
  Options,
  Domains,
  Themes,
  Keywords,
  Config,
  Laboraties,
  SponsorshipType,
} from './types'
import { sponsorshipApi } from './services/sponsorship'

export type EventDetails = Record<
  string,
  {
    event: Partial<Events> | null
    sessions: Partial<Sessions>[]
    subEvents: Partial<SubEvents>[]
    options: Partial<Options>[]
    step: 1 | 2 | 3 | 4 | 5
    id: string | null
    isDirty?: (boolean | undefined)[]
  }
>

export interface StoreState {
  token: string | null
  user: User | null
  eventDetails: EventDetails
  config: Config
  locations: Locations[]
  tags: Tags[]
  jobTitles: JobTitles[]
  keywords: Keywords[]
  laboratories: Laboraties[]
  sponsorshipTypes: SponsorshipType[]
  domains: Domains[]
  themes: Themes[]
  userSearch: User[]
}

export const initialState: StoreState = {
  token: null,
  user: null,
  eventDetails: {
    new: {
      event: null,
      sessions: [],
      subEvents: [],
      options: [],
      step: 1,
      id: null,
      isDirty: [],
    },
  },
  config: {
    default_admin_email: '',
    enums: {
      event: {
        status: [],
        type: [],
        organizer: [],
        attendance_type: [],
      },
      session: {
        status: [],
        organizer: [],
        type: [],
      },
      sub_event: {
        status: [],
        type: [],
      },
      option: {
        status: [],
        type: [],
      },
      sponsorship: {
        status: [],
        activity: [],
      },
      participant: {
        status: [],
        membership_level: [],
        home_region: [],
      },
      theme: {
        category: [],
      },
      contributor_roles: [],
    },
  },
  keywords: [],
  laboratories: [],
  sponsorshipTypes: [],
  locations: [],
  tags: [],
  jobTitles: [],
  domains: [],
  themes: [],
  userSearch: [],
}

const setTokenLogin = (state: StoreState, { payload }: { payload: LoginResponse }) => {
  Cookies.set('token', payload.key, { expires: 30, sameSite: 'strict', secure: true })
  state.token = payload.key
}

const resetOnLogout = (state: StoreState) => {
  Cookies.remove('token', { sameSite: 'strict', secure: true })
  Object.assign(state, deepClone(initialState))
}

const slice = createSlice({
  name: 'store',
  initialState,
  reducers: {
    setEventDetailsStep: (
      state,
      { payload: { data, key } }: { payload: { key: string; data: 1 | 2 | 3 | 4 | 5 } }
    ) => {
      set(state.eventDetails, `${key}.step`, data)
    },
    setEventDetailsId: (
      state,
      { payload: { data, key } }: { payload: { key: string; data: string | undefined } }
    ) => {
      set(state.eventDetails, `${key}.id`, data)
    },
    setEventDetailsDirtyState: (
      state,
      {
        payload: { data, step, key },
      }: { payload: { key: string; data: boolean; step: 1 | 2 | 3 | 4 | 5 } }
    ) => {
      set(state.eventDetails, `${key}.isDirty[${step}]`, data)
    },
    setEvent: (
      state,
      { payload: { data, key } }: { payload: { key: string; data: Partial<Events> } }
    ) => {
      set(state.eventDetails, `${key}.event`, deepClone(data))
    },
    setSessions: (
      state,
      { payload: { data, key } }: { payload: { key: string; data: Partial<Sessions>[] } }
    ) => {
      set(state.eventDetails, `${key}.sessions`, deepClone(data))
    },
    setSubEvents: (
      state,
      { payload: { data, key } }: { payload: { key: string; data: Partial<SubEvents>[] } }
    ) => {
      set(state.eventDetails, `${key}.subEvents`, deepClone(data))
    },
    setOptions: (
      state,
      { payload: { data, key } }: { payload: { key: string; data: Partial<Options>[] } }
    ) => {
      set(state.eventDetails, `${key}.options`, deepClone(data))
    },
    modifySubEventById: (
      state,
      {
        payload: { data, key, subEventId },
      }: {
        payload: { key: string; subEventId: number; data: Partial<SubEvents> }
      }
    ) => {
      const newSessionsId = data.sessions ?? []
      let oldSessionsId: number[] = []
      const eventDetails = get(state.eventDetails, key, []) as StoreState['eventDetails'][string]
      const subEventIndex =
        eventDetails?.subEvents?.findIndex(el => {
          if (el.id === subEventId) {
            oldSessionsId = el.sessions ?? []
            return true
          }
          return false
        }) ?? -1

      set(state.eventDetails, `${key}.subEvents[${subEventIndex}]`, deepClone(data))

      // Update each subEventId in sessions if sessionsId has changed
      const sessionsIdToRemoveSubEvent = oldSessionsId.filter(id => !newSessionsId.includes(id))
      const sessionsIdToAddSubEvent = newSessionsId.filter(id => !oldSessionsId.includes(id))
      sessionsIdToRemoveSubEvent.forEach(sessionId => {
        const newIds = (
          get(state.eventDetails, `${key}.sessions[${sessionId}].subEvents`, []) as number[]
        ).filter(id => id !== subEventId)
        set(state.eventDetails, `${key}.sessions[${sessionId}].subEvents`, newIds)
      })
      sessionsIdToAddSubEvent.forEach(sessionId => {
        const newIds = [
          ...(get(state.eventDetails, `${key}.sessions[${sessionId}].subEvents`, []) as number[]),
          subEventId,
        ]
        set(state.eventDetails, `${key}.sessions[${sessionId}].subEvents`, newIds)
      })
    },
    resetEventDetails: (state, { payload }: { payload: string }) => {
      set(state.eventDetails, payload, deepClone(initialState.eventDetails.new))
    },
    updateUserSearch: (state, { payload }: { payload: User[] }) => {
      state.userSearch = payload
    },
  },
  extraReducers: builder => {
    builder.addMatcher(authApi.endpoints.login.matchFulfilled, setTokenLogin)
    builder.addMatcher(authApi.endpoints.wordpressLogin.matchFulfilled, setTokenLogin)
    builder.addMatcher(authApi.endpoints.logout.matchFulfilled, resetOnLogout)
    builder.addMatcher(authApi.endpoints.logout.matchRejected, resetOnLogout)
    builder.addMatcher(userApi.endpoints.currentUser.matchFulfilled, (state, { payload }) => {
      state.user = payload
    })
    builder.addMatcher(optionsApi.endpoints.config.matchFulfilled, (state, { payload }) => {
      state.config = payload
    })
    builder.addMatcher(optionsApi.endpoints.listLocations.matchFulfilled, (state, { payload }) => {
      state.locations = payload
    })
    builder.addMatcher(optionsApi.endpoints.listTags.matchFulfilled, (state, { payload }) => {
      state.tags = payload
    })
    builder.addMatcher(optionsApi.endpoints.listJobTitles.matchFulfilled, (state, { payload }) => {
      state.jobTitles = payload
    })
    builder.addMatcher(optionsApi.endpoints.listDomains.matchFulfilled, (state, { payload }) => {
      state.domains = payload
    })
    builder.addMatcher(optionsApi.endpoints.listThemes.matchFulfilled, (state, { payload }) => {
      state.themes = payload
    })
    builder.addMatcher(optionsApi.endpoints.listKeywords.matchFulfilled, (state, { payload }) => {
      state.keywords = payload.results
    })
    builder.addMatcher(
      optionsApi.endpoints.listLaboratories.matchFulfilled,
      (state, { payload }) => {
        state.laboratories = payload
      }
    )
    builder.addMatcher(
      sponsorshipApi.endpoints.listSponsorshipsType.matchFulfilled,
      (state, { payload }) => {
        state.sponsorshipTypes = payload
      }
    )
  },
  selectors: {
    getCurrentUserToken: sliceState => sliceState.token,
    getCurrentUser: sliceState => deepClone(sliceState.user),
    isCurrentUserStaff: sliceState => !!sliceState.user?.is_staff,
    getEventDetails: sliceState => (key?: string) =>
      deepClone(get(sliceState.eventDetails, key ?? '', initialState.eventDetails.new)),
    getSubEventDetails:
      sliceState =>
      ({ key, sessionId, subEventId }: { key: string; sessionId: number; subEventId: number }) => {
        const eventDetails = get(
          sliceState.eventDetails,
          key,
          []
        ) as StoreState['eventDetails'][string]
        const session = eventDetails?.sessions?.find(el => el.id === sessionId)
        const subEvent = eventDetails?.subEvents?.find(el => el.id === subEventId)

        return deepClone({ session, subEvent })
      },
    getOptionsDetails: sliceState => (key: string) =>
      deepClone(get(sliceState.eventDetails, `${key}.options`, [])),
    isEventDetailsDirty: sliceState => (key: string) => {
      const dirtyState = get(
        sliceState.eventDetails,
        `${key}.isDirty`,
        []
      ) as StoreState['eventDetails'][string]['isDirty']
      return !!dirtyState?.some(Boolean)
    },
    getLocations: sliceState => sliceState.locations,
    getTags: sliceState => sliceState.tags,
    getJobTitles: sliceState => sliceState.jobTitles,
    getDomains: sliceState => sliceState.domains,
    getThemes: sliceState => sliceState.themes,
    getKeywords: sliceState => sliceState.keywords,
    getLaboratories: sliceState => sliceState.laboratories,
    getEventStatus: sliceState => sliceState.config.enums.event.status,
    getEventType: sliceState => sliceState.config.enums.event.type,
    getEventOrganizer: sliceState => sliceState.config.enums.event.organizer,
    getAttendanceType: sliceState => sliceState.config.enums.event.attendance_type,
    getSessionStatus: sliceState => sliceState.config.enums.session.status,
    getSubEventStatus: sliceState => sliceState.config.enums.sub_event.status,
    getSubEventType: sliceState => sliceState.config.enums.sub_event.type,
    getOptionType: sliceState => sliceState.config.enums.option.type,
    getOptionStatus: sliceState => sliceState.config.enums.option.status,
    getParticipantStatus: sliceState => sliceState.config.enums.participant.status,
    getMembershipLevel: sliceState => sliceState.config.enums.participant.membership_level,
    getHomeRegion: sliceState => sliceState.config.enums.participant.home_region,
    getThemeCategory: sliceState => sliceState.config.enums.theme.category,
    getContributorRoles: sliceState => sliceState.config.enums.contributor_roles,
    getSessionType: sliceState => sliceState.config.enums.session.type,
    getSponsorshipStatus: sliceState => sliceState.config.enums.sponsorship.status,
    getSponsorshipActivity: sliceState => sliceState.config.enums.sponsorship.activity,
    getSponsorshipType: sliceState => sliceState.sponsorshipTypes,
  },
})

export const {
  setEvent,
  setSessions,
  setSubEvents,
  setOptions,
  setEventDetailsStep,
  setEventDetailsId,
  setEventDetailsDirtyState,
  resetEventDetails,
  modifySubEventById,
} = slice.actions

export const {
  getCurrentUserToken,
  getCurrentUser,
  getContributorRoles,
  getEventDetails,
  getEventOrganizer,
  getEventStatus,
  getEventType,
  getAttendanceType,
  getHomeRegion,
  getJobTitles,
  getKeywords,
  getLaboratories,
  getLocations,
  getMembershipLevel,
  getOptionStatus,
  getOptionType,
  getParticipantStatus,
  getSessionStatus,
  getSessionType,
  getSubEventStatus,
  getSubEventType,
  getSponsorshipActivity,
  getSponsorshipStatus,
  getSponsorshipType,
  getSubEventDetails,
  getTags,
  getThemes,
  getThemeCategory,
  getDomains,
  getOptionsDetails,
  isEventDetailsDirty,
  isCurrentUserStaff,
} = slice.selectors

export default slice.reducer
